/* eslint-disable complexity */
import {
  getFetchArguments,
  retryRequestData
} from './retryRequestData'
import type {
  HandleError,
  HandleSuccess,
  Headers,
  Method,
  Params,
  ResponseType
} from './types'

const buildHeader = (
  method: string,
  isUrlEncoded: boolean
) => {
  const usesBody = method !== 'get' && method !== 'delete'

  return !isUrlEncoded && usesBody
    ? {
      'Content-Type': 'application/json' // otherwise it will default to text/plain
    }
    : undefined
}

const handleOkResponse = (responseType: ResponseType, response: Response) => {
  if (responseType === 'blob') return response.blob() // an uncaught error will crash

  if (responseType === 'text') return response.text()

  return response.json().catch(() => ({}))
}

const requestData = (
  baseUrl: string,
  method: Method,
  handleSuccess: HandleSuccess,
  handleError: HandleError,
  params: Params | undefined | any[],
  responseType: ResponseType,
  isUrlEncoded: boolean,
  extraHeaders?: Headers,
  formData?: FormData
) => {
  const headers = { ...extraHeaders, ...buildHeader(method, isUrlEncoded) }

  const isGet = method === 'get'

  // Only make a request cancellable if it is a GET
  const controller = isGet
    ? new AbortController()
    : undefined

  const baseConfig = {
    // only uppercase methods names are guaranteed acceptance
    method: method.toUpperCase(),
    headers,
    signal: controller && controller.signal
  }

  // Params can affect URL or config, so this method returns both
  const [url, config] = getFetchArguments(baseUrl, method, baseConfig, params, isUrlEncoded, formData)

  const catchFetchError = (error: { name: string; toString: () => any }) => {
    // Aborts are errors, even if intended
    if (error.name === 'AbortError') return

    handleError({
      status: 'Error',
      title: 'Network',
      detail: error.toString()
    })
  }

  const handleResponse = async (
    response: Response,
    retryCount: number = 0
  ): Promise<void> => {
    if (response.ok) {
      // Successful response
      return handleSuccess(await handleOkResponse(responseType, response))
    }

    // Handle 401 error with retries
    if (response.status === 401 && retryCount < 3) {
      await retryRequestData(
        method,
        isUrlEncoded,
        baseUrl,
        params,
        handleResponse,
        catchFetchError,
        retryCount,
        response,
        handleError,
        controller,
        extraHeaders,
        formData
      )

      // eslint-disable-next-line consistent-return
      return
    }

    // If not retriable or retries exhausted, handle error
    return handleError(await response.json().catch(() => ({})))
  }

  fetch(url as RequestInfo, config as RequestInit)
    .then(handleResponse, catchFetchError)
    // using a catch here will swallow errors that happen after done

  // can be called to cancel the request upon component cleanup
  return controller
    ? () => { controller.abort() } // must be wrapped
    : () => {}
}

export default requestData
